<template>
  <b-form v-if="!loading" @submit="onSubmit" novalidate>
    <b-form-row>
      <b-col cols="12" style="margin-bottom: 1rem;">
          
        <b-form-row class="correction-form-row gk-input-light">
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Name*')" label-for="inv-corr-name">
              <b-form-input data-analytics-click id="inv-corr-name" v-model="correction.name" :state="isValid('correction_name')" required :placeholder="$gettext('Correction')" />
              <b-form-invalid-feedback :state="isValid('correction_name')">
                {{ $gettext('This field is required.') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Period*')" label-for="inv-corr-period">
              <PeriodSelector v-if="defaultPeriodSelector" :default-period="defaultPeriodSelector" id="inv-corr-period" @input="setNewCorrectionDate" />
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" label="Customer" label-for="inv-corr-customer">
              <CustomerSelector 
                v-model="invoice.recipient_customer"
                id="inv-corr-customer"
                :filters="{ is_customer:'True', owner_company: currentCompany.id, is_archived:'False' }"
                hide-new-btn
                @change="selectedCustomer"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Project')" label-for="input-10">
              <b-form-select v-model="invoice.project" :disabled="projectList.length === 0">
                <b-form-select-option v-for="project in projectList" :value="project.id" :key="project.id">
                  {{project.name}}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row class="correction-form-row correction-invoice-lines">
          <b-col cols="12">
            <div class="content--inner" style="min-height: 300px;">
              <InvoiceLines 
                v-model="invoice.invoice_lines"
                :invoice="invoice"
                is-correction
                :show-error="isValid('invoice_lines') === false"
                ref="invoice-lines"
              />
            </div>
          </b-col>
        </b-form-row>

        <b-form-row class="correction-form-row gk-input-light">
          <b-col cols="12" lg="12">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Description')" label-for="inv-corr-description">
              <b-form-textarea v-model="invoice.message" id="inv-corr-description" rows="3" />
            </b-form-group>
          </b-col>
        </b-form-row>

      </b-col>
      <b-col cols="12">

          <b-form-row class="correction-form-row dropzone-row">
            <b-col cols="12" lg="12" class="d-flex flex-column h-100">
              <LoadingSpinner v-if="uploadingFiles" :display-text="$gettext('Uploading correction. This might take a while.')" />

              <div style="margin-bottom: 1rem;">
                {{ $gettext('Total file size:') }} <span :class="{ 'text-danger': maxSizeExceeded }">{{ niceBytes(totalFileSize, 1000, true) }}</span> / {{ niceBytes(maxTotalFileSize*1000*1000, 1000, true) }}
              </div>
              
              <FileUploader
                ref="file-uploader"
                showsize
                :file-size-max="maxTotalFileSize"
                @updated-queued-files="updateInvoiceFiles"
              />

              <div v-if="maxSizeExceeded" class="text-danger" style="font-size: 14px; padding: 0.5rem; margin-top: 1rem;">
                {{ $gettextInterpolate('Total size of files (%{ size }) exceeds upload limit. Please make sure your uploads do not exceed %{ max } MB.', { size: niceBytes(totalFileSize, 1000, true), max: maxTotalFileSize }) }}
              </div>
              
            </b-col>
          </b-form-row>

      </b-col>
    </b-form-row>

    <b-form-row style="margin-top: 1rem;">
      <b-col>
        <GekkoButton 
          type="submit"
          :text="$gettext('Add correction')" 
          icon="fa-solid fa-plus"
          :disabled="isSaving"
        />
      </b-col>
    </b-form-row>
  </b-form>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from "vuex";
import { niceBytes } from "@/utils/";
import GekkoButton from "@/components/GekkoButton";
import FileUploader from "@/components/ui/FileUploader";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import CustomerSelector from "@/components/CustomerSelector";
import PeriodSelector from "@/components/ui/PeriodSelector";
import InvoiceLines from "@/components/invoices/ui/InvoiceLines";

import InvoiceService from "@/store/services/invoice.service.js";
import CorrectionService from "@/store/services/correction.service.js";
import DocumentUpload from "@/store/services/document.upload.service.js";
import CustomerService from "@/store/services/customer.service.js";

export default {
  name: "EditCorrection",
  components: {
    GekkoButton,
    FileUploader,
    LoadingSpinner,
    CustomerSelector,
    PeriodSelector,
    InvoiceLines
  },
  props: {
    transactionInvoice: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      invoice: null,
      correction: null,

      created_new_invoice: null,

      invoiceFiles: null,
      uploadingFiles: false,

      RETENTION_RATES: [15, 7, 2, 1, 0],

      fromInvoice: false,
      correctionName: null,

      defaultPeriodSelector: null,

      maxTotalFileSize: 10,

      projectList: [],
      loading: true,
      isSaving: false,
      isSubmitted: false,
      requiredFields: ['correction_name', 'invoice_lines']
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      currentUserAccessRights: 'main/getCurrentAccessRights',
      companyTaxRates: "invoices/getCompanyTaxRates"
    }),
    totalFileSize () {
      let totalFileSize = 0
      if (this.invoiceFiles) {
        for (var i = 0; i < this.invoiceFiles.length; i++) {
          totalFileSize += this.invoiceFiles[i].size;
        }
      }

      // TODO Existing files size calculation for edit correction 
      // if (this.existingDocumentList) {
      //   for (var i = 0; i < this.existingDocumentList.length; i++) {
      //     this.totalFileSize += this.existingDocumentList[i].totalFileSize;
      //   }
      // }

      return totalFileSize
    },
    invoiceTotalAmount () {
      let sum = 0
      if (this.invoice.invoice_lines) {
        for (var i = 0; i < this.invoice.invoice_lines.length; i++) {
          sum = sum + this.invoice.invoice_lines[i].amount;
        }
        sum = Math.round(sum * 100) / 100; //make sure we have only 2 decimals
      }
      return sum
    },
    maxSizeExceeded () {
      return (this.totalFileSize > (this.maxTotalFileSize*1000*1000))
    },
  },
  created () {
    this.loadInvoicesGlobalData().then(() => {
      this.setupData()
      this.loading = false
    })
  },
  methods: {
    ...mapActions ({ 
      loadInvoicesGlobalData: "invoices/loadInvoicesGlobalData"
    }),

    /**
     * Init, prepares and sets invoice and lines default data
     */
    setupData () {
      // const invoiceDate = (this.defaultCorrectionDate || (new Date().format('Y-m-d')))
      const invoiceDate = new Date().format('Y-m-d')
      const now = new Date().format('Y-m-d H-i-s-u')

      this.invoice = {
        number: this.currentCompany.id + '_' + now.toString(),
        invoice_date: invoiceDate,
        payment_term: 0,
        status: 's',
        get_display_status: 'Sent',
        status_short: 's',
        sender_company: this.currentCompany.id,
        vat_reversed_charge: false,
        line_amount_is_gross: true,
        recipient_customer: null,
        invoice_lines: []
      }
      
      let correctionName = ''
      if (this.fromInvoice) {
        correctionName = this.$gettext('Correction for ') + this.fromInvoice
      } else {
        if (this.currentUserAccessRights.user_is_of_connected_accountant) {
          correctionName = this.$gettext('Accountant Correction')
        } else {
          correctionName = this.$gettext('Correction')
        }
      }

      if (this.transactionInvoice) {
        // set correction name
        correctionName = this.$gettext('Correction for ') + this.transactionInvoice.description

        // set correction period
        this.defaultPeriodSelector = {
          month: moment(this.transactionInvoice.date, "YYYY-MM-DD").month() + 1,
          year: moment(this.transactionInvoice.date, "YYYY-MM-DD").year()
        }

        // set correction amount
        const def_line = {
          order: 0,
          multiplier: 1,
          amount: this.transactionInvoice.amount,
          subtotal_amount: this.transactionInvoice.amount,
          tax_rate: this.currentCompany.default_tax_rate,
          description: ''
        }
        this.invoice.invoice_lines.push(def_line)
      } else {
        const today = new Date()
        this.defaultPeriodSelector = { month: today.getMonth(), year: today.getFullYear() }
      }

      this.correction = {
        name: correctionName,
        timeframe: moment(invoiceDate, "YYYY-MM-DD").format('MMMM YYYY')
      }

      this.invoice.retention_tax_percentage = this.RETENTION_RATES[this.RETENTION_RATES.length-1]
      if (this.currentCompany.companysettings.default_invoice_retention) {
          this.invoice.retention_tax_percentage = this.currentCompany.companysettings.default_invoice_retention
      }

      // TODO No Customer key?

      // Customer.list({
      //     owner_company: vm.isAccountant ? vm.customerCompany : vm.owner_company,
      //     extended: 'True',
      //     is_archived: 'False'
      // }).then(function (response) {
      //     vm.customers = {};
      //     for (var i = 0; i < response.data.length; i++) {
      //         vm.customers[response.data[i].id] = response.data[i];
      //     }
      //     vm.customers[0] = {id: 0, name: gettext("No Customer")};
      //     vm.invoice.recipient_customer = vm.customers[0];
      //     if (vm.edit) {
      //         var params = {
      //             extended: 'True'
      //         };
      //         vm.retrieveInvoice($state.params.id, params)
      //     }
      // });
    },
    /**
     * Sets a new 'invoice.invoice_date' and 'correction.timeframe'
     * 'invoice.invoice_date' is set to last day of the selected month
     * 'correction.timeframe' format is 'MMMM YYYY' (e.g. 'March 2023')
     * @param {object} newDate new selected date via PeriodSelector component
     */
    setNewCorrectionDate (newDate) {
      this.invoice.invoice_date = new Date(newDate.to.addDays(-1)).format('Y-m-d');
      this.correction.timeframe = moment(this.invoice.invoice_date, "YYYY-MM-DD").format('MMMM YYYY')
    },
    /**
     * Starts save process. Default submit form prevented
     */
    onSubmit (e) {
      this.isSaving = true
      this.isSubmitted = true
      e.preventDefault()
      this.createCorrection()
    },
    /**
     * Checks if a field is valid or empty/invalid if form is not yet submitted
     * 
     * @param {string} field field identifier on this.requiredFields array
     * @returns {boolean} false if field is invalid, null for valid (bootstrap :state prop contextual state)
     */
    isValid (field) {
      let isValid
      switch (field) {
        case 'correction_name':
          isValid = (this.correction.name && this.correction.name != '') ? null : false
          break;
        case 'invoice_lines':
          isValid = (this.invoice.invoice_lines.length > 1) ? null : false
          break;
      }
      if (!this.isSubmitted) isValid = null
      return isValid
    },
    /**
     * Checks validates this.requiredFields with isValid method
     * 
     * @returns {boolean} false if any field is empty or invalid
     */
    formIsValid () {
      let formErrors = []
      this.requiredFields.forEach(field => {
        formErrors.push(this.isValid(field))
      })
      return !formErrors.some((x) => x === false)
    },
    /**
     * Creates a paid invoice via InvoiceService
     *    then creates a correction via CorrectionService
     *    then uploads any files via DocumentUpload
     *    after that redirects to corresponding page
     */
    createCorrection () {
      console.log('createCorrection')

      if (this.formIsValid()) {
        this.prepareInvoiceLines()
        this.invoice.total_amount = this.invoiceTotalAmount

        if (!this.invoice.recipient_customer) {
          this.invoice.recipient_customer = this.currentCompany.id
        }

        console.log('invoice ->', this.invoice)

        InvoiceService.createPaidInvoice(this.invoice).then((response) => {
          this.created_new_invoice = response.data
          this.correction.invoice = response.data.id;

          CorrectionService.create(this.correction).then((response) => {
            const attached_to = response.data;

            // TODO Mixpanel send create event

            // mixpanel.track("Correction: create", {
            //     'url': location.href
            // });

            console.log('correction attached to ->', attached_to)
                    
            const description = this.$gettext('document uploaded for invoice %(name)s');
            const uploadData = {
              attached_to: attached_to,
              files: this.invoiceFiles,
              company_profile: this.currentCompany.id,
              description: description.replace("%(name)s", attached_to.name)
            }
            if (this.invoiceFiles && this.invoiceFiles.length > 0) {
              this.uploadingFiles = true
              DocumentUpload.bulkUpload(uploadData).then(() => {
                console.log('DocumentUpload complete')
                this.uploadingFiles = false

                // TODO Upload complete redirections
                
                // if(!this.isAccountant) {
                //     if (this.onCorrectionMainAction) {
                //         this.onCorrectionMainAction({ 'correction': this.correction });
                //     } else { 
                //         $window.location.href = Urls["invoices_display_invoices"]();
                //     }
                // }else{
                //     $state.go('accountant-invoice-list',{companyProfileId:this.customerCompany })
                // }

                this.$emit('created', this.created_new_invoice);

                this.isSaving = false
              });
            } else {
              this.isSaving = false
              this.$emit('created', this.created_new_invoice);
            }

          }).catch((error) => {
            this.uploadingFiles = false
            console.log('catch 2 reject', error)

            // TODO Upload error handling

            // init();
            // if (response.status!=400){
            //     this.uploadError = response.data['detail'];
            // }else{
            //     this.errors = response.data;
            //     this.uploadError = gettext("Error when uploading. Please make sure this file type is allowed and correct shown errors.");
            // }

          });
        })
      } else {
        this.isSaving = false
      }
    },
    /**
     * Removes the last item from this.invoice.invoice_lines array
     * which is suposed to be just a placeholder
     * Also removes subtotal_amount amount from all invoice_lines (neccesary?)
     */
    prepareInvoiceLines () {
      this.invoice.invoice_lines.pop()
      // why this
      // for (var i = 0; i < this.invoice.invoice_lines.length; i++) {
      //   delete this.invoice.invoice_lines[i].subtotal_amount
      // }
    },
    /**
     * Gets Dropzone Files from fileUploader component and sets this.invoiceFiles
     */
    updateInvoiceFiles () {
      this.invoiceFiles = this.$refs['file-uploader'].getDropzoneFiles()
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    /**
     * Gets customer's project list when a customer is selected
     * if none, projectList is emptied and invoice.project set to null
     */
    selectedCustomer () {
      if (this.invoice.recipient_customer) {
        CustomerService.retrieve(this.invoice.recipient_customer, { extended: true }).then((response) => {
          this.invoice.project = null
          this.projectList = response.data.projects
          if (this.projectList.length > 0) {
            this.projectList.unshift({ name: this.$gettext('Nothing Selected'), id: null })
          }        
        })
      } else {
        this.projectList.splice(0)
      }
    }
  }
}
</script>

<style lang="scss">
.upload-correction-form {
  display: flex;
  .gk-modal-label {
    margin-bottom: 0;
    font-size: 14px;
    color: #808080;
  }
  form { max-width: 900px; margin: 0 auto; }
  .correction-form-row {
    padding: 1rem;
    background-color: lighten($colorBlue, 44%);
    border-radius: $radiusMd;
    border: 1px solid $colorBorderDash;
    margin-right: 0;
    margin-left: 0;
    &:not(:last-child) { margin-bottom: 1rem; }
    &.correction-invoice-lines {
      border: 0;
      background: 0;
      padding: 0;
      > [class*=col-] { padding: 0; }
    }
    &.dropzone-row {
      display: block;
      height: 100%;
      border-radius: 8px;
      background: #F1F1F1;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
    }
  }
}
</style>
